import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';

const EventAnketoResults = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [responses, setResponses] = useState([]);
    const [selectedResponse, setSelectedResponse] = useState(null);
    const [eventQuestions, setEventQuestions] = useState([]);
    const [eventName, setEventName] = useState("");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const fetchData = async (isManualRefresh = false) => {
        if (isManualRefresh) setRefreshing(true);
        setError("");
        try {
            const t = Date.now();
            const [eventResult, resResult] = await Promise.allSettled([
                api.get(`/events/${eventId}/?_t=${t}`),
                api.get(`/events/${eventId}/get_responses/?_t=${t}`),
            ]);

            if (eventResult.status === "fulfilled") {
                setEventQuestions(eventResult.value.data.anketo_questions || []);
                setEventName(eventResult.value.data.eventName || "");
            } else {
                setError("イベント情報の取得に失敗しました。");
                console.error(eventResult.reason);
            }

            if (resResult.status === "fulfilled") {
                setResponses(resResult.value.data || []);
            } else {
                console.error("回答一覧の取得に失敗:", resResult.reason);
                setResponses([]);
            }
        } catch (err) {
            setError("データの取得に失敗しました。");
            console.error(err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
        const timer = setInterval(() => fetchData(), 60000);
        return () => clearInterval(timer);
    }, [eventId]); // fetchData は eventId 依存のローカル関数のため再定義不要

    // 回答を配列形式で取得（オブジェクト or 配列どちらでも対応）
    const getAnswerList = (answers) => {
        if (!answers) return [];
        if (Array.isArray(answers)) return answers;
        // {0: "...", 1: "..."} 形式
        const keys = Object.keys(answers).sort((a, b) => Number(a) - Number(b));
        return keys.map(k => answers[k]);
    };

    // 質問データから表示テキストを取得（文字列でもオブジェクト {question, options} でも対応）
    const getQuestionText = (q, fallbackIndex) => {
        if (!q) return `質問 ${fallbackIndex + 1}`;
        if (typeof q === "object") return String(q.question || q.q || q.text || `質問 ${fallbackIndex + 1}`);
        return String(q);
    };

    // 回答データから表示テキストを取得（配列選択肢などオブジェクトの可能性に対応）
    const getAnswerText = (ans) => {
        if (ans === null || ans === undefined) return "(未回答)";
        if (Array.isArray(ans)) return ans.join(", ") || "(未回答)";
        if (typeof ans === "object") return JSON.stringify(ans);
        return String(ans) || "(未回答)";
    };

    if (loading) return <div className="container mt-4"><p>読み込み中...</p></div>;

    const tableCellStyle = { padding: "12px 20px", verticalAlign: "middle" };
    // 見出しは枠の中で sticky にするため、tr ではなく th 自体に不透明な背景を持たせる
    const tableHeadStyle = { ...tableCellStyle, whiteSpace: "nowrap", background: "#1e3a5f" };

    return (
        <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: "40px 24px 60px", boxSizing: "border-box" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            {/* ヘッダー */}
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px", flexWrap: "wrap" }}>
                <button
                    onClick={() => navigate("/event/anketo")}
                    style={{
                        whiteSpace: "nowrap",
                        background: "#6b7280",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "8px 18px",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        cursor: "pointer",
                    }}
                >
                    ← 前画面に戻る
                </button>
                <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700, color: "#111827", flex: 1 }}>
                    アンケート回答一覧{eventName ? `：${eventName}` : ""}
                </h3>
                <button
                    onClick={() => fetchData(true)}
                    disabled={refreshing}
                    style={{
                        whiteSpace: "nowrap",
                        background: refreshing ? "#9ca3af" : "#2563eb",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "8px 18px",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        cursor: refreshing ? "not-allowed" : "pointer",
                        minWidth: 110,
                    }}
                >
                    {refreshing ? "更新中..." : "🔄 再読み込み"}
                </button>
                <span style={{ fontSize: "0.8rem", color: "#9ca3af", whiteSpace: "nowrap" }}>
                    回答数: {responses.length}件
                </span>
            </div>

            {error && (
                <div className="alert alert-danger">{error}</div>
            )}

            {/* 質問一覧（イベント未開催でも必ず表示） */}
            <div style={{
                background: "#fff", borderRadius: 10, padding: "16px 24px",
                marginBottom: 20, fontSize: "0.93rem",
                border: "1px solid #e5e7eb",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)"
            }}>
                <strong style={{ color: "#374151" }}>質問一覧：</strong>
                {eventQuestions.length === 0 ? (
                    <p style={{ color: "#9ca3af", margin: "8px 0 0 0" }}>質問が設定されていません。</p>
                ) : (
                    <ol style={{ margin: "8px 0 0 0", paddingLeft: 24, lineHeight: 2 }}>
                        {eventQuestions.map((q, i) => {
                            const text = (q && typeof q === "object")
                                ? String(q.question || q.q || q.text || `質問 ${i + 1}`)
                                : String(q || `質問 ${i + 1}`);
                            return <li key={i} style={{ color: "#4b5563" }}>{text}</li>;
                        })}
                    </ol>
                )}
            </div>

            {/* 回答者リスト。overflow: hidden だと右側が切れて見えず、見出しも固定されないため、
                枠の中で縦横スクロールさせる（bugfix_管理画面テーブルレイアウト_20261005.md BUG-601・604） */}
            <div className="adm-table-scroll" style={{
                background: "#fff", borderRadius: 10,
                border: "1px solid #e5e7eb",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)"
            }}>
            {responses.length === 0 ? (
                <p style={{ textAlign: "center", color: "#6c757d", padding: "48px 0" }}>
                    まだ回答がありません。
                </p>
            ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
                    <thead>
                        <tr style={{ background: "#1e3a5f", color: "#fff" }}>
                            <th style={tableHeadStyle}>社員コード</th>
                            <th style={tableHeadStyle}>氏名</th>
                            <th style={tableHeadStyle}>部署</th>
                            <th style={tableHeadStyle}>送信日時</th>
                            <th style={{ ...tableHeadStyle, width: 120 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {responses.map((res, idx) => (
                            <React.Fragment key={idx}>
                            <tr style={{
                                background: selectedResponse === res ? "#eff6ff" : (idx % 2 === 0 ? "#fff" : "#f9fafb"),
                                borderBottom: "1px solid #e5e7eb"
                            }}>
                                <td style={tableCellStyle}>{res.employee_id}</td>
                                <td style={{ ...tableCellStyle, fontWeight: 600 }}>{res.employee_name}</td>
                                <td style={tableCellStyle}>{res.department || "-"}</td>
                                <td style={{ ...tableCellStyle, color: "#6b7280", fontSize: "0.88rem" }}>{res.submitted_at}</td>
                                <td style={{ ...tableCellStyle, textAlign: "center" }}>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => setSelectedResponse(selectedResponse === res ? null : res)}
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        {selectedResponse === res ? "閉じる" : "回答を見る"}
                                    </button>
                                </td>
                            </tr>

                            {/* インライン回答詳細 */}
                            {selectedResponse === res && (
                                <tr style={{ background: "#eff6ff" }}>
                                    <td colSpan={5} style={{ padding: "20px 32px" }}>
                                        <div style={{ fontWeight: 700, marginBottom: 12, color: "#1e3a5f" }}>
                                            {res.employee_name} さんの回答
                                        </div>
                                        {getAnswerList(res.answers).length === 0 ? (
                                            <p style={{ color: "#6c757d" }}>回答データがありません。</p>
                                        ) : (
                                            getAnswerList(res.answers).map((ans, index) => (
                                                <div key={index} style={{ marginBottom: 16 }}>
                                                    <p style={{ fontWeight: 600, marginBottom: 4, color: "#374151" }}>
                                                        Q{index + 1}. {getQuestionText(eventQuestions[index], index)}
                                                    </p>
                                                    <p style={{
                                                        paddingLeft: 14, color: "#1d4ed8",
                                                        borderLeft: "3px solid #93c5fd",
                                                        margin: 0, whiteSpace: "pre-wrap",
                                                        overflowWrap: "anywhere",
                                                        lineHeight: 1.7
                                                    }}>
                                                        {getAnswerText(ans)}
                                                    </p>
                                                </div>
                                            ))
                                        )}
                                    </td>
                                </tr>
                            )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            )}
            </div>
        </div>
        </div>
    );
};

export default EventAnketoResults;
